import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserProfile from './UserProfile';
import AboutUs from './AboutUs';

const titles = {
  settings: 'Settings',
  account: 'Account settings',
  plans: 'Payment & plans',
  'app-settings': 'App settings',
  'account-info': 'Account information',
  about: 'About us',
  help: 'Help',
  'app-info': 'App information',
  'delete-account': 'Delete account',
};

const SettingsDrawer = ({
  open,
  onClose,
  onEnterEditMode,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState('settings');

  useEffect(() => {
    if (!open) setScreen('settings');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        if (screen === 'settings') onClose();
        else setScreen(screen === 'plans' || screen === 'app-settings' || screen === 'account-info' ? 'account' : 'settings');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, screen, onClose]);

  if (!open) return null;

  const back = () => {
    if (screen === 'settings') {
      onClose();
      return;
    }
    if (['plans', 'app-settings', 'account-info'].includes(screen)) {
      setScreen('account');
      return;
    }
    setScreen('settings');
  };

  const navigateAndClose = (path) => {
    onClose();
    navigate(path);
  };

  const enterEdit = async () => {
    const ok = await onEnterEditMode?.();
    if (ok !== false) onClose();
  };

  const Row = ({ title, subtitle, onClick, danger = false }) => (
    <button type="button" className={`settings-drawer-row${danger ? ' is-danger' : ''}`} onClick={onClick}>
      <span className="settings-drawer-row-text">
        <span className="settings-drawer-row-title">{title}</span>
        {subtitle && <span className="settings-drawer-row-subtitle">{subtitle}</span>}
      </span>
      <span className="settings-drawer-chevron">›</span>
    </button>
  );

  const renderSettings = () => (
    <div className="settings-drawer-groups">
      <div className="settings-drawer-group">
        <Row title="Account settings" onClick={() => setScreen('account')} />
        <Row title="Boards" onClick={() => navigateAndClose('/boards')} />
        <Row title="Edit mode" subtitle="Edit the current board or your first board." onClick={enterEdit} />
        <Row title="Presets" onClick={() => navigateAndClose('/presets')} />
      </div>

      <div className="settings-drawer-group">
        <Row title="About us" onClick={() => setScreen('about')} />
        <Row title="Help" onClick={() => setScreen('help')} />
        <Row title="App information" onClick={() => setScreen('app-info')} />
      </div>

      <div className="settings-drawer-group">
        <Row title="Delete account" danger onClick={() => setScreen('delete-account')} />
        <Row title="Logout" danger onClick={onLogout} />
      </div>
    </div>
  );

  const renderAccount = () => (
    <div className="settings-drawer-group">
      <Row title="Payment & plans" onClick={() => setScreen('plans')} />
      <Row title="App settings" subtitle="Sticky Core, voice and custom symbols." onClick={() => setScreen('app-settings')} />
      <Row title="Account information" subtitle="Name, email, PIN and password." onClick={() => setScreen('account-info')} />
    </div>
  );

  const renderContent = () => {
    switch (screen) {
      case 'account': return renderAccount();
      case 'plans': return <UserProfile view="plans" />;
      case 'app-settings': return <UserProfile view="app-settings" />;
      case 'account-info': return <UserProfile view="account-info" />;
      case 'about': return <AboutUs embedded onOpenFeedback={() => setScreen('help')} />;
      case 'help': return <UserProfile view="help" />;
      case 'app-info': return <UserProfile view="app-info" />;
      case 'delete-account': return <UserProfile view="delete-account" />;
      case 'settings':
      default: return renderSettings();
    }
  };

  return (
    <div className="settings-drawer-backdrop" onMouseDown={onClose}>
      <aside className="settings-drawer" onMouseDown={(e) => e.stopPropagation()} aria-label="Settings">
        <div className="settings-drawer-header">
          <button type="button" className="settings-drawer-back" onClick={back}>
            ‹ {screen === 'settings' ? 'Close' : 'Settings'}
          </button>
          <div className="settings-drawer-title">{titles[screen] || 'Settings'}</div>
          <div className="settings-drawer-header-spacer" />
        </div>
        <div className="settings-drawer-body">
          {renderContent()}
        </div>
      </aside>
    </div>
  );
};

export default SettingsDrawer;
